import { cn } from "@/lib/utils";
import { InputHTMLAttributes, forwardRef } from "react";
import { Icon } from "./icon";

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  ({ className, ...props }, ref) => (
    <input
      ref={ref}
      className={cn(
        "block w-full rounded-lg border border-outline-variant bg-surface-container-low px-3 py-3 text-body-md text-on-surface outline-none input-transition placeholder:text-outline focus:border-primary focus:bg-surface-container-lowest focus:ring-2 focus:ring-primary disabled:cursor-not-allowed disabled:opacity-60",
        className,
      )}
      {...props}
    />
  ),
);
Input.displayName = "Input";

export const Label = ({
  className,
  ...props
}: React.LabelHTMLAttributes<HTMLLabelElement>) => (
  <label
    className={cn("block text-label-md uppercase text-on-surface-variant tracking-wider", className)}
    {...props}
  />
);

export const Select = forwardRef<
  HTMLSelectElement,
  React.SelectHTMLAttributes<HTMLSelectElement>
>(({ className, children, ...props }, ref) => (
  <select
    ref={ref}
    className={cn(
      "block w-full rounded-lg border border-outline-variant bg-surface-container-low px-3 py-3 text-body-md text-on-surface outline-none input-transition focus:border-primary focus:bg-surface-container-lowest focus:ring-2 focus:ring-primary",
      className,
    )}
    {...props}
  >
    {children}
  </select>
));
Select.displayName = "Select";

export const SelectWithIcon = forwardRef<
  HTMLSelectElement,
  React.SelectHTMLAttributes<HTMLSelectElement>
>(({ className, children, ...props }, ref) => (
  <div className="relative">
    <select
      ref={ref}
      className={cn(
        "block w-full appearance-none rounded-lg border border-outline-variant bg-surface-container-low py-3 pl-4 pr-10 text-body-md text-on-surface outline-none input-transition focus:border-primary focus:ring-2 focus:ring-primary",
        className,
      )}
      {...props}
    >
      {children}
    </select>
    <Icon
      name="expand_more"
      size="md"
      className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-outline"
    />
  </div>
));
SelectWithIcon.displayName = "SelectWithIcon";

export const Textarea = forwardRef<
  HTMLTextAreaElement,
  React.TextareaHTMLAttributes<HTMLTextAreaElement>
>(({ className, ...props }, ref) => (
  <textarea
    ref={ref}
    className={cn(
      "block min-h-[88px] w-full rounded-lg border border-outline-variant bg-surface-container-low px-3 py-3 text-body-md text-on-surface outline-none input-transition placeholder:text-outline focus:border-primary focus:bg-surface-container-lowest focus:ring-2 focus:ring-primary",
      className,
    )}
    {...props}
  />
));
Textarea.displayName = "Textarea";

export const InputWithIcon = forwardRef<
  HTMLInputElement,
  InputHTMLAttributes<HTMLInputElement> & {
    icon: string;
    trailing?: React.ReactNode;
  }
>(({ className, icon, trailing, ...props }, ref) => (
  <div className="relative">
    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
      <Icon name={icon} size="md" className="text-outline" />
    </div>
    <input
      ref={ref}
      className={cn(
        "block w-full rounded-lg border border-outline-variant bg-surface-container-low py-3 pl-10 pr-4 text-body-md text-on-surface outline-none input-transition placeholder:text-outline focus:border-primary focus:bg-surface-container-lowest focus:ring-2 focus:ring-primary",
        trailing && "pr-10",
        className,
      )}
      {...props}
    />
    {trailing ? (
      <div className="absolute inset-y-0 right-0 flex items-center pr-3">{trailing}</div>
    ) : null}
  </div>
));
InputWithIcon.displayName = "InputWithIcon";

export function FilterTabs<T extends string>({
  options,
  value,
  onChange,
}: {
  options: { label: string; value: T }[];
  value: T;
  onChange: (value: T) => void;
}) {
  return (
    <div className="inline-flex rounded-lg border border-outline-variant bg-surface-container-lowest p-1 shadow-card">
      {options.map(({ label, value: optionValue }) => (
        <button
          key={optionValue}
          type="button"
          onClick={() => onChange(optionValue)}
          className={cn(
            "rounded-md px-4 py-2 text-body-md font-medium transition-all",
            value === optionValue
              ? "bg-primary text-on-primary shadow-sm"
              : "text-on-surface-variant hover:bg-surface-container-low hover:text-on-surface",
          )}
        >
          {label}
        </button>
      ))}
    </div>
  );
}
